/**
 * iter845i — Self-Service Online Ordering — customer-facing page.
 *
 * Routes:
 *   /order/:branchSlug              — direct link (customer picks Dine-In or Pickup)
 *   /order/:branchSlug/t/:tableToken — QR scan (auto Dine-In + table pre-filled)
 *   /order/:branchSlug/track/:orderId — thank-you + live status
 *
 * No auth. Mobile-first. All data comes from /api/public/menu/{slug} and
 * order submission POSTs to /api/public/order.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { ShoppingCart, Plus, Minus, ChevronLeft, Utensils, ShoppingBag, X, Phone, Mail, User, StickyNote, CheckCircle2, Clock, Store, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const PMLabel = {
  cash_on_pickup: { label: 'Cash on Pickup', icon: '💵' },
  pay_at_counter: { label: 'Pay at Counter', icon: '🧾' },
  online_card: { label: 'Card / Apple Pay', icon: '💳' },
};

const currencySym = (code) => ({ SAR: 'SAR', USD: '$', EUR: '€', GBP: '£', AED: 'AED', INR: '₹' }[code] || code);

export default function PublicOrderPage() {
  const { branchSlug, tableToken } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, branch: null, items: [], categories: [], error: null });
  const [cart, setCart] = useState([]); // [{item_id, name, price, quantity}]
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderType, setOrderType] = useState('takeaway');
  const [contact, setContact] = useState({ name: '', phone: '', email: '' });
  const [notes, setNotes] = useState('');
  const [payment, setPayment] = useState('cash_on_pickup');
  const [submitting, setSubmitting] = useState(false);
  const [thankYou, setThankYou] = useState(null); // { order_number, total, estimated_ready_min, ... }

  // Load menu
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const url = tableToken
          ? `${API}/public/menu/${branchSlug}/t/${tableToken}`
          : `${API}/public/menu/${branchSlug}`;
        const { data } = await axios.get(url);
        if (cancel) return;
        setState({ loading: false, branch: data.branch, items: data.items, categories: data.categories, error: null });
        // QR scan → default to dine-in when table is present, else default to takeaway.
        if (data.branch.table) setOrderType('dine_in');
        else if (data.branch.pickup_enabled) setOrderType('takeaway');
        else if (data.branch.dine_in_enabled) setOrderType('dine_in');
        // Prime payment method to the first enabled option.
        const pms = data.branch.payment_methods || [];
        if (pms.length > 0) setPayment(pms[0]);
      } catch (err) {
        if (cancel) return;
        setState({ loading: false, branch: null, items: [], categories: [], error: err.response?.data?.detail || 'Unable to load menu' });
      }
    };
    load();
    return () => { cancel = true; };
  }, [branchSlug, tableToken]);

  const cur = currencySym(state.branch?.currency_code);
  const taxRate = state.branch?.tax_rate || 15;

  const filteredItems = useMemo(() => {
    if (selectedCategory === 'all') return state.items;
    return state.items.filter((it) => it.category === selectedCategory);
  }, [selectedCategory, state.items]);

  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.quantity, 0), [cart]);
  const cartTax = useMemo(() => cartTotal * (taxRate / 100), [cartTotal, taxRate]);
  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.quantity, 0), [cart]);

  // One cart line per item + size; size price is additive (same as the POS) and re-priced by the server.
  const addToCart = (item, size = null) => {
    const key = `${item.id}|${size?.name || ''}`;
    setCart((prev) => {
      const existing = prev.find((c) => c.key === key);
      if (existing) return prev.map((c) => c.key === key ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { key, item_id: item.id, size: size?.name || null, name: size ? `${item.name} (${size.name})` : item.name,
        price: item.price + (size?.price || 0), quantity: 1 }];
    });
    toast.success(`${item.name}${size ? ` (${size.name})` : ''} added`);
  };
  const updateQty = (key, delta) => {
    setCart((prev) => prev
      .map((c) => c.key === key ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c)
      .filter((c) => c.quantity > 0));
  };
  const removeItem = (key) => setCart((prev) => prev.filter((c) => c.key !== key));

  const submitOrder = async () => {
    if (!contact.name.trim() || !contact.phone.trim()) {
      toast.error('Name and phone are required.');
      return;
    }
    if (state.branch?.require_email && !contact.email.trim()) {
      toast.error('Email is required at this branch.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        slug: branchSlug,
        qr_token: tableToken || null,
        order_type: orderType,
        items: cart.map((c) => ({ item_id: c.item_id, quantity: c.quantity, modifiers: c.size ? [{ type: 'size', name: c.size }] : [] })),
        contact: {
          name: contact.name.trim(),
          phone: contact.phone.trim(),
          email: contact.email.trim() || null,
        },
        notes: notes.trim() || null,
        payment_method: payment,
      };
      const { data } = await axios.post(`${API}/public/order`, payload);
      if (data?.requires_payment && data?.payment_url) {
        // MyFatoorah hosted redirect — customer completes payment on
        // MyFatoorah then lands on /order/:slug/payment/callback.
        toast.message('Redirecting to secure payment…');
        window.location.href = data.payment_url;
        return;
      }
      setThankYou(data);
      setCart([]);
      setShowCheckout(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Rendering ────────────────────────────────────────────────────
  if (state.loading) {
    return <div className="min-h-screen bg-stone-50 flex items-center justify-center text-stone-500">Loading menu…</div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-4"><X size={28} /></div>
          <div className="text-lg font-bold text-stone-800 mb-1">Ordering unavailable</div>
          <div className="text-sm text-stone-600">{state.error}</div>
        </div>
      </div>
    );
  }
  if (thankYou) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-stone-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center bg-white rounded-3xl shadow-xl p-6 sm:p-8" data-testid="thank-you-panel">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} />
          </div>
          <h1 className="text-2xl font-bold text-stone-900">{thankYou.thank_you}</h1>
          <div className="mt-4 grid grid-cols-2 gap-3 text-left">
            <div className="bg-stone-50 rounded-2xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-stone-500">Order #</div>
              <div className="text-lg font-bold" data-testid="thank-you-order-number">{thankYou.order_number}</div>
            </div>
            <div className="bg-stone-50 rounded-2xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-stone-500">Total</div>
              <div className="text-lg font-bold">{thankYou.currency_code} {thankYou.total.toFixed(2)}</div>
            </div>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-800 text-sm font-semibold px-4 py-2">
            <Clock size={14} /> Ready in ~{thankYou.estimated_ready_min} min
          </div>
          {thankYou.confirmation_sent && Object.keys(thankYou.confirmation_sent).length > 0 && (
            <div className="mt-4 text-xs text-stone-600">
              Confirmation: {Object.entries(thankYou.confirmation_sent).map(([k, v]) => `${k} ${v.startsWith('sent') ? '✓' : '·'}`).join(' · ')}
            </div>
          )}
          <button
            onClick={() => { setThankYou(null); }}
            className="mt-6 h-11 w-full rounded-full bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800 transition-colors"
            data-testid="new-order-btn"
          >
            Place another order
          </button>
        </div>
      </div>
    );
  }

  const b = state.branch;
  if (!b.is_open) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4"><Clock size={28} /></div>
          <div className="text-lg font-bold">{b.branch_name} is currently closed</div>
          <div className="text-sm text-stone-600 mt-1">Please check back during ordering hours.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-32" data-testid="public-order-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-stone-900 to-stone-800 text-white px-4 py-4 sticky top-0 z-30 shadow-md">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><Store size={18} /></div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold truncate" data-testid="branch-name">{b.branch_name}</h1>
            <div className="text-[11px] text-stone-300 flex items-center gap-2 flex-wrap">
              {b.location && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {b.location}</span>}
              <span className="inline-flex items-center gap-1"><Clock size={10} /> Ready ~{b.prep_time_min} min</span>
              {b.table && <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-semibold text-[10px]" data-testid="table-badge">📍 {b.table.name}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Category chips */}
      <div className="max-w-3xl mx-auto px-4 py-3 overflow-x-auto flex gap-2 sticky top-[68px] bg-stone-50/95 backdrop-blur-md z-20" data-testid="category-strip">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold transition-colors ${selectedCategory === 'all' ? 'bg-stone-900 text-white' : 'bg-white text-stone-600 border border-stone-200'}`}
          data-testid="category-all"
        >All</button>
        {state.categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setSelectedCategory(c.id)}
            className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold transition-colors ${selectedCategory === c.id ? 'bg-stone-900 text-white' : 'bg-white text-stone-600 border border-stone-200'}`}
            data-testid={`category-${c.id}`}
          >{c.name}</button>
        ))}
      </div>

      {/* Menu items */}
      <div className="max-w-3xl mx-auto px-4 pt-2 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="items-grid">
        {filteredItems.length === 0 ? (
          <div className="col-span-full text-center py-16 text-stone-400">No items in this category.</div>
        ) : filteredItems.map((it) => {
          const sizes = it.size_options || [];
          const inCart = sizes.length ? null : cart.find((c) => c.key === `${it.id}|`);
          return (
            <div
              key={it.id}
              className="bg-white rounded-2xl shadow-sm p-3 flex gap-3 items-start hover:shadow-md transition-shadow"
              data-testid={`item-${it.id}`}
            >
              {it.image_url ? (
                <img src={it.image_url} alt={it.name} className="w-20 h-20 rounded-xl object-cover shrink-0" />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-stone-100 flex items-center justify-center shrink-0"><Utensils size={22} className="text-stone-400" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-stone-900 truncate">{it.name}</div>
                {it.description && <div className="text-[11px] text-stone-500 line-clamp-2 mt-0.5">{it.description}</div>}
                {(it.allergens || []).length > 0 && (
                  <div className="text-[10px] text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 mt-1 inline-block" data-testid={`item-${it.id}-allergens`}>
                    May contain: {it.allergens.map((a) => String(a).replace(/_/g, ' ')).join(', ')}
                  </div>
                )}
                {sizes.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`item-${it.id}-sizes`}>
                    {sizes.map((sz) => (
                      <button key={sz.name} onClick={() => addToCart(it, sz)}
                        className="h-8 px-3 rounded-full bg-stone-900 text-white text-xs font-semibold hover:bg-stone-800 transition-colors flex items-center gap-1"
                        data-testid={`item-${it.id}-size-${sz.name}`}>
                        <Plus size={12} /> {sz.name} · {cur} {(it.price + sz.price).toFixed(2)}
                      </button>
                    ))}
                  </div>
                ) : (
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm font-bold text-stone-900">{cur} {it.price.toFixed(2)}</span>
                  {inCart ? (
                    <div className="flex items-center gap-1 bg-stone-100 rounded-full p-0.5">
                      <button onClick={() => updateQty(inCart.key, -1)} className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center" data-testid={`item-${it.id}-dec`}><Minus size={12} /></button>
                      <span className="text-xs font-bold w-5 text-center" data-testid={`item-${it.id}-qty`}>{inCart.quantity}</span>
                      <button onClick={() => updateQty(inCart.key, 1)} className="w-7 h-7 rounded-full bg-stone-900 text-white shadow-sm flex items-center justify-center" data-testid={`item-${it.id}-inc`}><Plus size={12} /></button>
                    </div>
                  ) : (
                    <button
                      onClick={() => addToCart(it)}
                      className="h-8 px-3 rounded-full bg-stone-900 text-white text-xs font-semibold hover:bg-stone-800 transition-colors flex items-center gap-1"
                      data-testid={`item-${it.id}-add`}
                    ><Plus size={12} /> Add</button>
                  )}
                </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sticky cart bar */}
      {cart.length > 0 && !showCheckout && (
        <div className="fixed bottom-3 left-3 right-3 max-w-2xl mx-auto z-40" data-testid="sticky-cart-bar">
          <button
            onClick={() => setShowCheckout(true)}
            className="w-full h-14 rounded-2xl bg-stone-900 text-white flex items-center justify-between px-4 shadow-2xl hover:bg-stone-800 transition-colors"
            data-testid="review-order-btn"
          >
            <span className="inline-flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center relative">
                <ShoppingCart size={14} />
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-400 text-stone-900 text-[9px] font-bold flex items-center justify-center" data-testid="cart-count">{cartCount}</span>
              </div>
              <span className="font-semibold text-sm">Review order</span>
            </span>
            <span className="font-bold text-sm" data-testid="cart-total-bar">{cur} {(cartTotal + cartTax).toFixed(2)}</span>
          </button>
        </div>
      )}

      {/* Checkout sheet */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setShowCheckout(false)}>
          <div className="w-full sm:max-w-md bg-white sm:rounded-3xl rounded-t-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="checkout-sheet">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-stone-100 sticky top-0 bg-white z-10">
              <button onClick={() => setShowCheckout(false)} className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center"><ChevronLeft size={18} /></button>
              <div className="font-bold">Your order</div>
              <div className="w-8" />
            </div>
            <div className="p-4 space-y-4">
              {/* Order type */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-2">Order type</div>
                <div className="grid grid-cols-2 gap-2">
                  {b.dine_in_enabled && (
                    <button
                      onClick={() => setOrderType('dine_in')}
                      className={`h-14 rounded-2xl border-2 flex flex-col items-center justify-center gap-1 transition-colors ${orderType === 'dine_in' ? 'border-stone-900 bg-stone-900 text-white' : 'border-stone-200 bg-white text-stone-600'}`}
                      data-testid="order-type-dine_in"
                    >
                      <Utensils size={16} />
                      <span className="text-[11px] font-semibold">Dine-In</span>
                    </button>
                  )}
                  {b.pickup_enabled && (
                    <button
                      onClick={() => setOrderType('takeaway')}
                      className={`h-14 rounded-2xl border-2 flex flex-col items-center justify-center gap-1 transition-colors ${orderType === 'takeaway' ? 'border-stone-900 bg-stone-900 text-white' : 'border-stone-200 bg-white text-stone-600'}`}
                      data-testid="order-type-takeaway"
                    >
                      <ShoppingBag size={16} />
                      <span className="text-[11px] font-semibold">Pickup</span>
                    </button>
                  )}
                </div>
                {b.table && orderType === 'dine_in' && (
                  <div className="mt-2 text-[11px] text-stone-500">Your table: <span className="font-bold text-stone-900">{b.table.name}</span></div>
                )}
              </div>

              {/* Items */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-2">Items ({cartCount})</div>
                <div className="space-y-2">
                  {cart.map((c) => (
                    <div key={c.key} className="flex items-center justify-between bg-stone-50 rounded-2xl p-3" data-testid={`cart-line-${c.key}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{c.name}</div>
                        <div className="text-[11px] text-stone-500">{cur} {c.price.toFixed(2)} × {c.quantity}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => updateQty(c.key, -1)} className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center"><Minus size={12} /></button>
                        <span className="text-xs font-bold w-5 text-center">{c.quantity}</span>
                        <button onClick={() => updateQty(c.key, 1)} className="w-7 h-7 rounded-full bg-stone-900 text-white shadow-sm flex items-center justify-center"><Plus size={12} /></button>
                        <button onClick={() => removeItem(c.key)} className="w-7 h-7 ml-1 rounded-full text-rose-500 flex items-center justify-center"><X size={13} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-2">Contact info</div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 bg-stone-50 rounded-2xl px-3 h-11">
                    <User size={14} className="text-stone-400 shrink-0" />
                    <input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Full name *" className="flex-1 bg-transparent outline-none text-sm" data-testid="contact-name" />
                  </div>
                  <div className="flex items-center gap-2 bg-stone-50 rounded-2xl px-3 h-11">
                    <Phone size={14} className="text-stone-400 shrink-0" />
                    <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Phone (WhatsApp) *" className="flex-1 bg-transparent outline-none text-sm" data-testid="contact-phone" />
                  </div>
                  <div className="flex items-center gap-2 bg-stone-50 rounded-2xl px-3 h-11">
                    <Mail size={14} className="text-stone-400 shrink-0" />
                    <input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder={b.require_email ? 'Email *' : 'Email (optional)'} className="flex-1 bg-transparent outline-none text-sm" data-testid="contact-email" />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-2">Notes for the kitchen</div>
                <div className="flex items-start gap-2 bg-stone-50 rounded-2xl px-3 py-2.5">
                  <StickyNote size={14} className="text-stone-400 shrink-0 mt-0.5" />
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Allergies, spice level, no onions…"
                    className="flex-1 bg-transparent outline-none text-sm min-h-[52px]"
                    data-testid="notes-input"
                  />
                </div>
              </div>

              {/* Payment */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-2">Payment</div>
                <div className="space-y-2">
                  {(b.payment_methods || []).map((pm) => (
                    <button
                      key={pm}
                      onClick={() => setPayment(pm)}
                      className={`w-full h-12 rounded-2xl border-2 flex items-center gap-3 px-4 transition-colors ${payment === pm ? 'border-stone-900 bg-stone-50' : 'border-stone-200 bg-white'}`}
                      data-testid={`payment-${pm}`}
                    >
                      <span className="text-lg">{PMLabel[pm]?.icon || '💳'}</span>
                      <span className="text-sm font-semibold flex-1 text-left">{PMLabel[pm]?.label || pm}</span>
                      {payment === pm && <CheckCircle2 size={16} className="text-stone-900" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="rounded-2xl bg-stone-50 p-3 space-y-1">
                <div className="flex justify-between text-xs text-stone-600"><span>Subtotal</span><span>{cur} {cartTotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-xs text-stone-600"><span>{b.tax_name} ({taxRate}%)</span><span>{cur} {cartTax.toFixed(2)}</span></div>
                <div className="flex justify-between text-base font-bold pt-2 border-t border-stone-200"><span>Total</span><span>{cur} {(cartTotal + cartTax).toFixed(2)}</span></div>
              </div>

              {/* Submit */}
              <button
                onClick={submitOrder}
                disabled={submitting || cart.length === 0}
                className="w-full h-12 rounded-2xl bg-stone-900 text-white font-bold text-sm hover:bg-stone-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="place-order-btn"
              >
                {submitting ? 'Placing order…' : `Place order · ${cur} ${(cartTotal + cartTax).toFixed(2)}`}
              </button>
              <div className="text-[10px] text-stone-400 text-center">Ready in ~{b.prep_time_min} min · powered by Smarto</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
